<script setup lang="ts">
  import { ref, computed, useTemplateRef } from 'vue'
  import type {
    AddCustomFieldRequestPayload,
    UpdateCustomFieldRequestPayload
  } from '@gat-ship/types'
  import type { ResolvedField } from '../model/types'
  import {
    Button,
    DataList,
    SectionHeader,
    Empty,
    EmptyContent,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle
  } from '@gat-ship/vue-components'
  import { useToast } from '@gat-ship/vue-components'
  import { IconPlus, IconCoffee, IconArrowUpDown } from '@gat-ship/icons'
  import { useCustomFieldsView } from '../composables/useCustomFieldsView'
  import CustomFieldListItem from '../components/CustomFieldListItem.vue'
  import CustomFieldModalAdd from '../components/modals/CustomFieldModalAdd.vue'
  import CustomFieldModalEdit from '../components/modals/CustomFieldModalEdit.vue'
  import CustomFieldModalReorder from '../components/modals/CustomFieldModalReorder.vue'

  const { toast } = useToast()

  const {
    selectedTarget,
    loading,
    loadingFieldIds,
    resolvedFields,
    addCustomField,
    updateCustomField,
    deleteCustomField,
    reorderCustomFields
  } = useCustomFieldsView()

  // ── Filtered Fields ───────────────────────────────────────────

  const filteredFields = computed(() =>
    resolvedFields.value.filter((f) => f.origin === 'custom')
  )

  function isFieldLoading(field: ResolvedField) {
    return (
      !!field.customFieldId && loadingFieldIds.value.has(field.customFieldId)
    )
  }

  // ── Dialog State ──────────────────────────────────────────────

  const showAddDialog = ref(false)
  const addDialogRef =
    useTemplateRef<InstanceType<typeof CustomFieldModalAdd>>('addDialog')

  const editDialogField = ref<ResolvedField | null>(null)
  const editDialogRef =
    useTemplateRef<InstanceType<typeof CustomFieldModalEdit>>('editDialog')

  const showReorderDialog = ref(false)

  // ── Handlers ──────────────────────────────────────────────────

  function handleFieldClick(field: ResolvedField) {
    editDialogField.value = field
    editDialogRef.value?.show(field)
  }

  async function handleAddCustomField(payload: AddCustomFieldRequestPayload) {
    try {
      await addCustomField(payload)
      toast({
        title: 'Custom field added',
        description: `"${payload.label}" has been added.`
      })
    } catch {
      addDialogRef.value?.showModal(payload)
      toast({
        title: 'Failed to add custom field',
        description: 'Please review the details and try again.',
        variant: 'destructive'
      })
    }
  }

  async function handleDeleteCustomField(fieldId: string) {
    await deleteCustomField(fieldId)
  }

  async function handleUpdateCustomField(
    payload: UpdateCustomFieldRequestPayload
  ) {
    if (!editDialogField.value?.customFieldId) return
    await updateCustomField(editDialogField.value.customFieldId, payload)
  }

  async function handleReorder(orderedIds: string[]) {
    try {
      await reorderCustomFields(orderedIds)
      toast({
        title: 'Field order updated',
        description: 'The custom field display order has been saved.'
      })
    } catch {
      toast({
        title: 'Failed to update field order',
        description: 'Please try again.',
        variant: 'destructive'
      })
    }
  }
</script>

<template>
  <SectionHeader
    title="Custom Fields"
    description="Configure custom fields for your organization."
  >
    <template #actions>
      <Button
        v-if="filteredFields.length > 1"
        size="lg"
        variant="outline"
        @click="showReorderDialog = true"
      >
        <IconArrowUpDown class="size-4" />
        Reorder
      </Button>
      <Button size="lg" @click="showAddDialog = true">
        <IconPlus class="size-4" />
        Add Custom Field
      </Button>
    </template>
  </SectionHeader>
  <DataList :loading="loading" :empty="filteredFields.length === 0">
    <CustomFieldListItem
      v-for="field in filteredFields"
      :key="field.fieldName"
      :field="field"
      :loading="isFieldLoading(field)"
      @select="handleFieldClick(field)"
    />
    <template #empty>
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <IconCoffee />
          </EmptyMedia>
          <EmptyTitle>No custom fields yet.</EmptyTitle>
          <EmptyDescription>
            Add a custom field to get started.
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button @click="showAddDialog = true">
            <IconPlus class="size-4" />
            Add Custom Field
          </Button>
        </EmptyContent>
      </Empty>
    </template>
  </DataList>

  <CustomFieldModalAdd
    ref="addDialog"
    v-model:open="showAddDialog"
    :target="selectedTarget"
    :existing-field-names="resolvedFields.map((f) => f.fieldName)"
    @add="handleAddCustomField"
  />

  <CustomFieldModalEdit
    ref="editDialog"
    :field="editDialogField"
    @update="handleUpdateCustomField"
    @delete="handleDeleteCustomField(editDialogField!.customFieldId!)"
  />

  <CustomFieldModalReorder
    v-model:open="showReorderDialog"
    :fields="filteredFields"
    @save="handleReorder"
  />
</template>
